import { useState, useRef, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { TSuggest } from 'feature/registration/types'
import { AddressErrorsType } from 'feature/addresses/types'
import { useDadataSuggests } from './useDadataSuggests'

interface UseDadataMultiSelectLogicProps {
  name: string
}

export const useDadataMultiSelectLogic = ({
  name,
}: UseDadataMultiSelectLogicProps) => {
  const { trigger, getValues } = useFormContext()

  const [isDadataSelectVisible, setDadataSelectVisible] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [isInputVisible, setInputVisible] = useState(() => {
    const currentValue = getValues(name)
    return !Array.isArray(currentValue) || currentValue.length === 0
  })
  const [inputError, setInputError] = useState<string | null>(null)

  const selectInputRef = useRef<HTMLInputElement | null>(null)
  const isInputVisibleRef = useRef(isInputVisible)

  const { suggests, isLoadingSuggest, getSuggests, clearSuggests } =
    useDadataSuggests({
      minQueryLength: 4,
      onError: () => setInputError('Ошибка сервера, попробуйте позже'),
    })

  const resetInputState = () => {
    setInputValue('')
    setDadataSelectVisible(false)
    clearSuggests()
    setInputError(null)
  }

  const updateInputError = () => {
    if (!suggests.length) {
      setInputError(AddressErrorsType.addressNotFound)
    } else {
      setInputError(null)
    }
  }

  useEffect(() => {
    isInputVisibleRef.current = isInputVisible
    if (isInputVisible) {
      selectInputRef.current?.focus()
      return
    }
    resetInputState()
  }, [isInputVisible])

  useEffect(() => {
    if (!isInputVisible || isLoadingSuggest || inputValue.length < 4) return
    updateInputError()
  }, [suggests, isLoadingSuggest, inputValue, isInputVisible])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\n/g, '').trim()
    setInputValue(value)
    setInputError(null)

    if (!value) {
      clearSuggests()
      setDadataSelectVisible(false)
      return
    }

    if (value.length < 4) {
      clearSuggests()
      return
    }

    if (value.length > 300) {
      setInputValue(value.slice(0, 300))
      setInputError(AddressErrorsType.addressIsLong)
      return
    }

    setDadataSelectVisible(true)
    getSuggests(value)
  }

  const selectHandler = async (
    item: TSuggest,
    onChange: (value: TSuggest[]) => void,
  ) => {
    const selectedValues = getValues(name) as TSuggest[]
    const isDuplicate = selectedValues.some(
      v => v.address === item.address && v.fiasId === item.fiasId,
    )
    if (isDuplicate) {
      setInputError(AddressErrorsType.addressAlreadyExist)
      return
    }

    const newValues = [...selectedValues, item]
    onChange(newValues)
    resetInputState()
    setInputVisible(false)
    await trigger(name)
  }

  const removeValue = (
    valueToRemove: TSuggest,
    selectedValues: TSuggest[],
    onChange: (value: TSuggest[]) => void,
  ) => {
    const newValues = selectedValues.filter(
      v =>
        v.address !== valueToRemove.address ||
        v.fiasId !== valueToRemove.fiasId,
    )
    onChange(newValues)
    if (!newValues.length) setInputVisible(true)
    trigger(name)
  }

  const onClickAwayHandler = () => {
    if (!isInputVisible) return
    resetInputState()
  }

  const onFocus = () => {
    if (inputValue.length >= 4 && isInputVisibleRef.current) {
      getSuggests(inputValue)
      setDadataSelectVisible(true)
    }
  }

  const handleCancel = () => {
    setInputVisible(false)
    resetInputState()
  }

  const openInput = () => {
    setInputVisible(true)
    resetInputState()
  }

  return {
    isInputVisible,
    inputValue,
    inputError,
    isDadataSelectVisible,
    isLoadingSuggest,
    suggests,
    selectInputRef,
    handleChange,
    selectHandler,
    onClickAwayHandler,
    onFocus,
    removeValue,
    handleCancel,
    openInput,
    setInputVisible,
    setInputError,
  }
}
